import { useState } from 'react'
import { Button, Input, Spin, Typography, message } from 'antd'
import { SaveOutlined } from '@ant-design/icons'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import axiosInstance from '@/lib/axios'

const { Text } = Typography
const { TextArea } = Input

// Nessuna mappa statica: titolo e ordine vengono dal DB tramite il campo `descrizione`.
// Il frontend si limita a visualizzare ciò che c'è, numerando i riquadri in ordine.

interface SettingEntry {
  chiave: string
  valore: string | null
  descrizione: string | null
  gruppo: string
}

export default function AdcClausoleSettings() {
  const [messageApi, contextHolder] = message.useMessage()
  const queryClient = useQueryClient()
  const [edited, setEdited] = useState<Record<string, string>>({})
  const [saving, setSaving] = useState<string | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['settings-adc-clausole'],
    queryFn: async () => {
      const res = await axiosInstance.get<Record<string, SettingEntry[]>>('/settings')
      return res.data?.adc_contratto ?? []
    },
  })

  const getValue = (chiave: string) => {
    if (chiave in edited) return edited[chiave]
    const entry = (data ?? []).find(s => s.chiave === chiave)
    return entry?.valore ?? ''
  }

  const handleSave = async (chiave: string) => {
    setSaving(chiave)
    try {
      await axiosInstance.put(`/settings/${chiave}`, { valore: getValue(chiave) })
      messageApi.success('Clausola salvata.')
      queryClient.invalidateQueries({ queryKey: ['settings-adc-clausole'] })
      setEdited(p => { const n = { ...p }; delete n[chiave]; return n })
    } catch {
      messageApi.error('Errore durante il salvataggio.')
    } finally {
      setSaving(null)
    }
  }

  // Ordine numerico sul suffisso (es. adc_clausola_10 dopo adc_clausola_9,
  // non tra adc_clausola_1 e adc_clausola_2 come farebbe l'ordine alfabetico).
  const sorted = [...(data ?? [])].sort((a, b) => {
    const na = parseInt(a.chiave.split('_').pop() ?? '0', 10)
    const nb = parseInt(b.chiave.split('_').pop() ?? '0', 10)
    return na - nb
  })

  if (isLoading) return <Spin />

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {contextHolder}
      <Text type="secondary">
        Queste clausole vengono stampate nel contratto di consegna Auto di Cortesia.
        Ogni clausola può essere modificata indipendentemente.
      </Text>
      {sorted.map((entry, idx) => {
        const { chiave, descrizione } = entry
        const val = getValue(chiave)
        const isDirty = chiave in edited
        const label = `${idx + 1}. ${descrizione ?? chiave}`
        return (
          <div key={chiave} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <Text strong style={{ fontSize: 13 }}>{label}</Text>
            <TextArea
              rows={4}
              value={val}
              onChange={e => setEdited(p => ({ ...p, [chiave]: e.target.value }))}
            />
            <Button
              type={isDirty ? 'primary' : 'default'}
              icon={<SaveOutlined />}
              loading={saving === chiave}
              disabled={!isDirty}
              onClick={() => handleSave(chiave)}
              style={{ alignSelf: 'flex-start' }}
            >
              {isDirty ? 'Salva' : 'Salvato'}
            </Button>
          </div>
        )
      })}
    </div>
  )
}
